import { Button, ButtonGroup, Colors, Spinner, Tag } from "@blueprintjs/core";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import { IFGCustomInputProps, useFGContext } from "nsitools-react";
import * as React from "react";
import { useQuery } from "react-query";
import styled from "styled-components";

import { LieuFormationSelectorDialog } from ".";
import { ClearButton } from "../..";
import { LieuFormationApi } from "../../../api";
import { ERoutes } from "../../../AppRouter";
import { useAuth } from "../../../contexts";
import { useApiService } from "../../../hooks";
import { FGReadOnlyInput } from "../FGReadOnlyInput";

const InputContainer = styled.div`
  padding: 0.4rem 0 0 0.6rem;
`;

const StyledTag = styled(Tag)`
  & a {
    color: ${Colors.WHITE};
  }
`;

const NamesContainer = styled.div`
  > * + * {
    margin-left: 5px;
  }
`;

export interface ILieuFormationSelectorFieldProps extends Omit<IFGCustomInputProps, "children"> {
  parentLieuFormationIds?: number[];
  disabled?: boolean;
  allowMultipleSelection?: boolean;
  ref?: React.Ref<HTMLInputElement>;
  idsiegeSocial?: number;
}

export const LieuFormationSelectorField: React.FunctionComponent<ILieuFormationSelectorFieldProps> = ({
  name,
  parentLieuFormationIds,
  label = null,
  disabled = false,
  className,
  allowMultipleSelection = false,
  ref,
  idsiegeSocial,
  ...fgCustomInputProps
}) => {
  const { hasPermission } = useAuth();
  const ctx = useFGContext();
  const api = useApiService(LieuFormationApi);

  const hasValue = React.useMemo(
    () =>
      (allowMultipleSelection && !isEmpty(ctx?.formik?.values?.[name])) ||
      (!allowMultipleSelection && !!ctx?.formik?.values?.[name]),
    [allowMultipleSelection, ctx?.formik?.values, name]
  );
  const [selectorDialogOpen, setSelectorDialogOpen] = React.useState(false);
  const [currentSelectedLieuFormationIds, setSelectedLieuFormationIds] = React.useState<number[]>([]);

  const { data: displayNames, isFetching: loadingDisplayNames } = useQuery(
    ["lieu-formation-display-names", currentSelectedLieuFormationIds],
    async () => api.lieuFormationGetDisplayNames({ request_body: currentSelectedLieuFormationIds })
  );

  const clearSelectedLieuFormation = React.useCallback(() => {
    ctx?.formik?.setFieldValue(name, allowMultipleSelection ? [] : null, true);
    setSelectedLieuFormationIds([]);
  }, [allowMultipleSelection, ctx?.formik, name]);

  React.useEffect(() => {
    if (ctx?.formik?.values?.[name]) {
      const value = ctx?.formik?.values?.[name];
      setSelectedLieuFormationIds(isArray(value) ? value : [value]);
    } else {
      setSelectedLieuFormationIds([]);
    }
  }, [ctx?.formik?.values, name]);

  const onLieuFormationsSelected = React.useCallback(
    (selectedLieuFormationIds?: number[]) => {
      if (selectedLieuFormationIds?.length > 0) {
        ctx?.formik?.setFieldTouched(name);
        ctx?.formik?.setFieldValue(
          name,
          allowMultipleSelection ? selectedLieuFormationIds : selectedLieuFormationIds[0],
          true
        );
        setSelectedLieuFormationIds(selectedLieuFormationIds);
      }
    },
    [allowMultipleSelection, ctx?.formik, name]
  );

  const selectorProps = React.useMemo(
    () => ({
      excludedLieuFormationIds: parentLieuFormationIds,
      allowMultipleSelection,
      preSelectedIds: currentSelectedLieuFormationIds
    }),
    [allowMultipleSelection, parentLieuFormationIds, currentSelectedLieuFormationIds]
  );

  const openLieuFormationSelector = React.useCallback(() => {
    setSelectorDialogOpen(true);
  }, []);

  const getEditRoute = React.useCallback(
    (id: number) =>
      `#${ERoutes.lieuFormation}/${id}/detail/${hasPermission("REPRESENTANT", "RW", "DETAIL") ? "edit" : "view"}`,
    [hasPermission]
  );

  const removeLieuFormationId = React.useCallback(
    (lieuFormationId: number) => {
      const newLieuFormationIds = [...currentSelectedLieuFormationIds];
      newLieuFormationIds.splice(currentSelectedLieuFormationIds.indexOf(lieuFormationId), 1);
      ctx?.formik?.setFieldTouched(name);
      ctx?.formik?.setFieldValue(name, newLieuFormationIds, true);
      setSelectedLieuFormationIds(newLieuFormationIds);
    },
    [ctx?.formik, currentSelectedLieuFormationIds, name]
  );

  const getLieuFormationLink = React.useCallback(
    (id: number, name: string, tags: boolean) => {
      const link = hasPermission("LIEUFORMATION", "R", "DETAIL") ? (
        <a href={getEditRoute(id)} target="_blank" rel="noreferrer" tabIndex={-1} key={id}>
          {name}
        </a>
      ) : (
        name
      );
      return tags ? (
        <StyledTag key={id} onRemove={() => removeLieuFormationId(id)}>
          {link}
        </StyledTag>
      ) : (
        link
      );
    },
    [getEditRoute, hasPermission, removeLieuFormationId]
  );

  const onClosed = React.useCallback(
    (selectedLieuFormationIds?: number[]) => {
      onLieuFormationsSelected(selectedLieuFormationIds);
      setSelectorDialogOpen(false);
    },
    [onLieuFormationsSelected]
  );

  return (
    <>
      <FGReadOnlyInput
        name={name}
        label={label}
        leftElement={
          <InputContainer>
            {loadingDisplayNames ? (
              <Spinner size={16} />
            ) : (
              <NamesContainer>
                {displayNames.map(dn => getLieuFormationLink(dn.id, dn.text, allowMultipleSelection))}
              </NamesContainer>
            )}
          </InputContainer>
        }
        rightElement={
          <ButtonGroup>
            {hasValue && <ClearButton onClick={clearSelectedLieuFormation} tabIndex={-1} disabled={disabled} />}
            <Button
              icon="office"
              minimal={true}
              onClick={openLieuFormationSelector}
              disabled={disabled}
              tabIndex={-1}
            />
          </ButtonGroup>
        }
        {...fgCustomInputProps}
      />
      <LieuFormationSelectorDialog
        onClosed={onClosed}
        onLieuFormationSelected={onClosed}
        selectorDialogOpen={selectorDialogOpen}
        idsiegeSocial={idsiegeSocial}
        {...selectorProps}
      />
    </>
  );
};
